import { useMutation } from '@tanstack/react-query';
import { produce } from 'immer';
import { z } from 'zod';
import { privacySettingQueryOptions } from '@/hooks/settings/use-privacy-settings';
import { type PrivacySettings, privacySettingTypeMap } from '@/models/mine/privacy-settings';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';

export default function useUpdatePrivacySettings() {
  const mutation = useMutation<RespondedType, ResponseError, UpdateVerifiedFriendRequest>({
    mutationKey: ['/UpdateVerifiedFriendSetting'],
    mutationFn: (params) => fetcher('/UpdateVerifiedFriendSetting', params, { validResponseCodes: [0] }),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: privacySettingQueryOptions.queryKey });

      const previousSettings = queryClient.getQueryData(privacySettingQueryOptions.queryKey);
      queryClient.setQueryData(privacySettingQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (!draft) return;
          switch (params.Type) {
            case 2:
              draft.IsManualVerifiedGroup = Boolean(params.IsManualVerified);
              break;
            case 1:
              draft.IsManualVerifiedFriend = Boolean(params.IsManualVerified);
              break;
          }
        }),
      );

      return { previousSettings };
    },
    onError: (err, _, context) => {
      const typedContext = context as { previousSettings?: PrivacySettings };
      if (typedContext?.previousSettings) {
        queryClient.setQueryData(privacySettingQueryOptions.queryKey, typedContext.previousSettings);
      }
      addToast({
        type: 'error',
        message: err.message || 'errors.error',
        translateMessage: !err.message,
      });
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });

  const updatePrivacySettings = (key: keyof PrivacySettings, value: boolean) => {
    mutation.mutate({
      Type: privacySettingTypeMap[key],
      IsManualVerified: value ? 1 : 0,
    });
  };

  return {
    updatePrivacySettings,
    ...mutation,
  };
}

const updateVerifiedFriendRequestScheme = z.object({
  IsManualVerified: z.number(),
  Type: z.number(),
});

type UpdateVerifiedFriendRequest = z.infer<typeof updateVerifiedFriendRequestScheme>;
